/**
 * 全局 Message 实例管理器
 * 独立模块，无任何依赖，避免循环引用
 */

import type { MessageInstance } from 'antd/es/message/interface';
import type React from 'react';

// 全局实例存储
let globalMessageInstance: MessageInstance | null = null;

/**
 * 设置全局 message 实例（仅由 app.tsx 调用）
 */
export function setGlobalMessage(instance: MessageInstance) {
  globalMessageInstance = instance;
}

/**
 * 获取全局 message 实例
 */
export function getGlobalMessage(): MessageInstance | null {
  return globalMessageInstance;
}

/**
 * 控制台兜底函数
 */
const consoleFallback = {
  success: (content: string) => console.log('✅ [Message Success]:', content),
  error: (content: string) => console.error('❌ [Message Error]:', content),
  info: (content: string) => console.info('ℹ️ [Message Info]:', content),
  warning: (content: string) => console.warn('⚠️ [Message Warning]:', content),
  loading: (content: string) => {
    console.log('⏳ [Message Loading]:', content);
    return () => console.log('✓ [Message Loading Done]');
  },
};

/**
 * 安全的 message 调用包装器
 * - 优先使用全局实例
 * - 实例未就绪时使用 console 兜底并给出警告
 * - 保留返回值（loading 等方法需要）
 * - 支持对象参数和字符串参数两种用法
 */
type MessageConfig = {
  content: string;
  duration?: number;
  onClose?: () => void;
  key?: React.Key;
};

type MessageArg = string | MessageConfig;

const normalizeMessageArg = (arg: MessageArg): MessageConfig => {
  if (typeof arg === 'string') {
    return { content: arg };
  }
  return arg;
};

export const message = {
  success: (arg: MessageArg, duration?: number, onClose?: () => void) => {
    if (globalMessageInstance) {
      if (typeof arg === 'string') {
        return globalMessageInstance.success(arg, duration, onClose);
      }
      return globalMessageInstance.success(arg);
    }
    console.warn('[globalMessage] Instance not ready, using console fallback');
    const config = normalizeMessageArg(arg);
    consoleFallback.success(config.content);
  },

  error: (arg: MessageArg, duration?: number, onClose?: () => void) => {
    if (globalMessageInstance) {
      if (typeof arg === 'string') {
        return globalMessageInstance.error(arg, duration, onClose);
      }
      return globalMessageInstance.error(arg);
    }
    console.warn('[globalMessage] Instance not ready, using console fallback');
    const config = normalizeMessageArg(arg);
    consoleFallback.error(config.content);
  },

  info: (arg: MessageArg, duration?: number, onClose?: () => void) => {
    if (globalMessageInstance) {
      if (typeof arg === 'string') {
        return globalMessageInstance.info(arg, duration, onClose);
      }
      return globalMessageInstance.info(arg);
    }
    console.warn('[globalMessage] Instance not ready, using console fallback');
    const config = normalizeMessageArg(arg);
    consoleFallback.info(config.content);
  },

  warning: (arg: MessageArg, duration?: number, onClose?: () => void) => {
    if (globalMessageInstance) {
      if (typeof arg === 'string') {
        return globalMessageInstance.warning(arg, duration, onClose);
      }
      return globalMessageInstance.warning(arg);
    }
    console.warn('[globalMessage] Instance not ready, using console fallback');
    const config = normalizeMessageArg(arg);
    consoleFallback.warning(config.content);
  },

  loading: (arg: MessageArg, duration?: number, onClose?: () => void) => {
    if (globalMessageInstance) {
      if (typeof arg === 'string') {
        return globalMessageInstance.loading(arg, duration, onClose);
      }
      return globalMessageInstance.loading(arg);
    }
    console.warn('[globalMessage] Instance not ready, using console fallback');
    const config = normalizeMessageArg(arg);
    return consoleFallback.loading(config.content);
  },

  open: (config: any) => {
    if (globalMessageInstance) {
      return globalMessageInstance.open(config);
    }
    console.warn('[globalMessage] Instance not ready, using console fallback');
    console.log('[Message]:', config);
  },

  destroy: (key?: React.Key) => {
    if (globalMessageInstance) {
      globalMessageInstance.destroy(key);
    }
  },
};

